// Serialize/parse the two files that make up an external app's theme:
// `/index.css` (CSS custom properties for light + dark) and
// `/tailwind.config.js` (shadcn-style color/font/radius mapping).
//
// These files are written as standard exportable code (see
// [[feedback-external-app-exportable]]) — anyone can `npm install && npm run
// build` the project outside our platform. The runtime preview bootstrap
// evaluates `tailwind.config.js` in-browser; in a real build PostCSS does the
// same job.

import type { AppTheme, AppThemeColors } from "./types";

// Mirror of the base44 light palette. Values are HSL space-separated triples
// (no `hsl()` wrapper, no commas) so they compose with Tailwind's alpha
// modifier syntax: `bg-primary/50` becomes `hsl(var(--primary) / 0.5)`.
export const DEFAULT_THEME_COLORS_LIGHT: AppThemeColors = {
  background: "220 20% 97%",
  foreground: "230 25% 10%",
  card: "0 0% 100%",
  cardForeground: "230 25% 10%",
  popover: "0 0% 100%",
  popoverForeground: "230 25% 10%",
  primary: "245 58% 51%",
  primaryForeground: "0 0% 100%",
  secondary: "220 14% 94%",
  secondaryForeground: "230 25% 15%",
  muted: "220 14% 94%",
  mutedForeground: "220 10% 46%",
  accent: "245 58% 96%",
  accentForeground: "245 58% 51%",
  destructive: "0 72% 51%",
  destructiveForeground: "0 0% 100%",
  border: "220 13% 90%",
  input: "220 13% 90%",
  ring: "245 58% 51%",
  chart1: "245 58% 51%",
  chart2: "173 58% 39%",
  chart3: "43 74% 49%",
  chart4: "12 76% 61%",
  chart5: "320 65% 52%",
  sidebarBackground: "0 0% 100%",
  sidebarForeground: "230 25% 10%",
  sidebarPrimary: "245 58% 51%",
  sidebarPrimaryForeground: "0 0% 100%",
  sidebarAccent: "245 58% 96%",
  sidebarAccentForeground: "245 58% 51%",
  sidebarBorder: "220 13% 90%",
  sidebarRing: "245 58% 51%",
};

const DEFAULT_DARK_PALETTE_BLOCK = `  .dark {
    --background: 230 20% 8%;
    --foreground: 220 14% 96%;
    --card: 230 18% 12%;
    --card-foreground: 220 14% 96%;
    --popover: 230 18% 12%;
    --popover-foreground: 220 14% 96%;
    --primary: 245 58% 60%;
    --primary-foreground: 0 0% 100%;
    --secondary: 230 15% 18%;
    --secondary-foreground: 220 14% 96%;
    --muted: 230 15% 18%;
    --muted-foreground: 220 10% 55%;
    --accent: 245 40% 20%;
    --accent-foreground: 245 58% 70%;
    --destructive: 0 62% 40%;
    --destructive-foreground: 0 0% 98%;
    --border: 230 15% 20%;
    --input: 230 15% 20%;
    --ring: 245 58% 60%;
    --chart-1: 245 58% 60%;
    --chart-2: 173 58% 45%;
    --chart-3: 43 74% 55%;
    --chart-4: 12 76% 65%;
    --chart-5: 320 65% 58%;
    --sidebar-background: 230 18% 10%;
    --sidebar-foreground: 220 14% 96%;
    --sidebar-primary: 245 58% 60%;
    --sidebar-primary-foreground: 0 0% 100%;
    --sidebar-accent: 245 40% 20%;
    --sidebar-accent-foreground: 245 58% 70%;
    --sidebar-border: 230 15% 20%;
    --sidebar-ring: 245 58% 60%;
  }`;

export const DEFAULT_THEME: AppTheme = {
  colors: DEFAULT_THEME_COLORS_LIGHT,
  fonts: { sans: "Inter" },
  radius: "0.625rem",
};

// Ordered list of (token-key, css-variable-name). Used both by the serializer
// and by the panel to lay out swatches in a stable order.
export const THEME_TOKENS: ReadonlyArray<{ key: keyof AppThemeColors; cssVar: string; label: string }> = [
  { key: "background", cssVar: "--background", label: "background" },
  { key: "foreground", cssVar: "--foreground", label: "foreground" },
  { key: "card", cssVar: "--card", label: "card" },
  { key: "cardForeground", cssVar: "--card-foreground", label: "card foreground" },
  { key: "popover", cssVar: "--popover", label: "popover" },
  { key: "popoverForeground", cssVar: "--popover-foreground", label: "popover foreground" },
  { key: "primary", cssVar: "--primary", label: "primary" },
  { key: "primaryForeground", cssVar: "--primary-foreground", label: "primary foreground" },
  { key: "secondary", cssVar: "--secondary", label: "secondary" },
  { key: "secondaryForeground", cssVar: "--secondary-foreground", label: "secondary foreground" },
  { key: "muted", cssVar: "--muted", label: "muted" },
  { key: "mutedForeground", cssVar: "--muted-foreground", label: "muted foreground" },
  { key: "accent", cssVar: "--accent", label: "accent" },
  { key: "accentForeground", cssVar: "--accent-foreground", label: "accent foreground" },
  { key: "destructive", cssVar: "--destructive", label: "destructive" },
  { key: "destructiveForeground", cssVar: "--destructive-foreground", label: "destructive foreground" },
  { key: "border", cssVar: "--border", label: "border" },
  { key: "input", cssVar: "--input", label: "input" },
  { key: "ring", cssVar: "--ring", label: "ring" },
  { key: "chart1", cssVar: "--chart-1", label: "chart 1" },
  { key: "chart2", cssVar: "--chart-2", label: "chart 2" },
  { key: "chart3", cssVar: "--chart-3", label: "chart 3" },
  { key: "chart4", cssVar: "--chart-4", label: "chart 4" },
  { key: "chart5", cssVar: "--chart-5", label: "chart 5" },
  { key: "sidebarBackground", cssVar: "--sidebar-background", label: "sidebar background" },
  { key: "sidebarForeground", cssVar: "--sidebar-foreground", label: "sidebar foreground" },
  { key: "sidebarPrimary", cssVar: "--sidebar-primary", label: "sidebar primary" },
  { key: "sidebarPrimaryForeground", cssVar: "--sidebar-primary-foreground", label: "sidebar primary foreground" },
  { key: "sidebarAccent", cssVar: "--sidebar-accent", label: "sidebar accent" },
  { key: "sidebarAccentForeground", cssVar: "--sidebar-accent-foreground", label: "sidebar accent foreground" },
  { key: "sidebarBorder", cssVar: "--sidebar-border", label: "sidebar border" },
  { key: "sidebarRing", cssVar: "--sidebar-ring", label: "sidebar ring" },
];

const GOOGLE_FONTS_HOST = "https://fonts.googleapis.com/css2";

function fontImportUrl(fontFamily: string): string {
  const family = fontFamily.replace(/\s+/g, "+");
  return `${GOOGLE_FONTS_HOST}?family=${family}:wght@300;400;500;600;700;800&display=swap`;
}

// --- index.css ---------------------------------------------------------------

export function buildIndexCss(theme: AppTheme, darkBlock: string = DEFAULT_DARK_PALETTE_BLOCK): string {
  const fontImport = `@import url('${fontImportUrl(theme.fonts.sans)}');`;
  const fontVarName = `--font-${theme.fonts.sans.toLowerCase().replace(/\s+/g, "-")}`;
  const lightLines = THEME_TOKENS.map(
    (t) => `    ${t.cssVar}: ${theme.colors[t.key]};`,
  ).join("\n");
  return `${fontImport}

@tailwind base;
@tailwind components;
@tailwind utilities;

@layer base {
  :root {
    ${fontVarName}: '${theme.fonts.sans}', sans-serif;
${lightLines}
    --radius: ${theme.radius};
  }

${darkBlock}
}

@layer base {
  * {
    @apply border-border outline-ring/50;
  }

  body {
    @apply bg-background text-foreground;
    font-family: var(${fontVarName});
  }
}
`;
}

// Parses --token: value; declarations out of a :root block. We don't try to
// be a full CSS parser — just match the form we wrote. Anything we can't
// recognize falls back to the default.
export function parseIndexCss(src: string): { colors: AppThemeColors; radius: string; fontSans: string; darkBlock: string } {
  const colors: AppThemeColors = { ...DEFAULT_THEME_COLORS_LIGHT };
  const rootMatch = src.match(/:root\s*\{([\s\S]*?)\n\s*\}/);
  const rootBody = rootMatch ? rootMatch[1] : "";
  for (const t of THEME_TOKENS) {
    const re = new RegExp(`${t.cssVar}\\s*:\\s*([^;]+);`);
    const m = rootBody.match(re);
    if (m) (colors as any)[t.key] = m[1].trim();
  }
  const radiusMatch = rootBody.match(/--radius\s*:\s*([^;]+);/);
  const radius = radiusMatch ? radiusMatch[1].trim() : DEFAULT_THEME.radius;
  const fontImportMatch = src.match(/family=([A-Za-z0-9+]+)/);
  const fontSans = fontImportMatch ? fontImportMatch[1].replace(/\+/g, " ") : DEFAULT_THEME.fonts.sans;
  const darkMatch = src.match(/\.dark\s*\{[\s\S]*?\n\s*\}/);
  const darkBlock = darkMatch ? `  ${darkMatch[0]}` : DEFAULT_DARK_PALETTE_BLOCK;
  return { colors, radius, fontSans, darkBlock };
}

// --- tailwind.config.js ------------------------------------------------------

export function buildTailwindConfig(theme: AppTheme): string {
  const fontVarName = `--font-${theme.fonts.sans.toLowerCase().replace(/\s+/g, "-")}`;
  return `/** @type {import('tailwindcss').Config} */
module.exports = {
  darkMode: ["class"],
  content: ["./index.html", "./src/**/*.{ts,tsx,js,jsx}"],
  theme: {
    extend: {
      fontFamily: {
        ${theme.fonts.sans.toLowerCase().replace(/\s+/g, "-")}: ['var(${fontVarName})'],
        sans: ['var(${fontVarName})', 'ui-sans-serif', 'system-ui'],
      },
      borderRadius: {
        lg: 'var(--radius)',
        md: 'calc(var(--radius) - 2px)',
        sm: 'calc(var(--radius) - 4px)',
      },
      colors: {
        background: 'hsl(var(--background))',
        foreground: 'hsl(var(--foreground))',
        card: {
          DEFAULT: 'hsl(var(--card))',
          foreground: 'hsl(var(--card-foreground))',
        },
        popover: {
          DEFAULT: 'hsl(var(--popover))',
          foreground: 'hsl(var(--popover-foreground))',
        },
        primary: {
          DEFAULT: 'hsl(var(--primary))',
          foreground: 'hsl(var(--primary-foreground))',
        },
        secondary: {
          DEFAULT: 'hsl(var(--secondary))',
          foreground: 'hsl(var(--secondary-foreground))',
        },
        muted: {
          DEFAULT: 'hsl(var(--muted))',
          foreground: 'hsl(var(--muted-foreground))',
        },
        accent: {
          DEFAULT: 'hsl(var(--accent))',
          foreground: 'hsl(var(--accent-foreground))',
        },
        destructive: {
          DEFAULT: 'hsl(var(--destructive))',
          foreground: 'hsl(var(--destructive-foreground))',
        },
        border: 'hsl(var(--border))',
        input: 'hsl(var(--input))',
        ring: 'hsl(var(--ring))',
        chart: {
          '1': 'hsl(var(--chart-1))',
          '2': 'hsl(var(--chart-2))',
          '3': 'hsl(var(--chart-3))',
          '4': 'hsl(var(--chart-4))',
          '5': 'hsl(var(--chart-5))',
        },
        sidebar: {
          DEFAULT: 'hsl(var(--sidebar-background))',
          foreground: 'hsl(var(--sidebar-foreground))',
          primary: 'hsl(var(--sidebar-primary))',
          'primary-foreground': 'hsl(var(--sidebar-primary-foreground))',
          accent: 'hsl(var(--sidebar-accent))',
          'accent-foreground': 'hsl(var(--sidebar-accent-foreground))',
          border: 'hsl(var(--sidebar-border))',
          ring: 'hsl(var(--sidebar-ring))',
        },
      },
      keyframes: {
        'accordion-down': {
          from: { height: '0' },
          to: { height: 'var(--radix-accordion-content-height)' },
        },
        'accordion-up': {
          from: { height: 'var(--radix-accordion-content-height)' },
          to: { height: '0' },
        },
      },
      animation: {
        'accordion-down': 'accordion-down 0.2s ease-out',
        'accordion-up': 'accordion-up 0.2s ease-out',
      },
    },
  },
  plugins: [require("tailwindcss-animate")],
};
`;
}

// --- color conversion (hex <-> HSL triple) -----------------------------------
//
// The panel's MUI color picker speaks hex; the files store HSL triples. These
// helpers bridge the two without introducing a heavy color library.

export function hslTripleToHex(triple: string): string {
  const m = triple.trim().match(/^(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%$/);
  if (!m) return "#000000";
  const h = parseFloat(m[1]);
  const s = parseFloat(m[2]) / 100;
  const l = parseFloat(m[3]) / 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = h / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r = 0, g = 0, b = 0;
  if (hp >= 0 && hp < 1) { r = c; g = x; }
  else if (hp < 2) { r = x; g = c; }
  else if (hp < 3) { g = c; b = x; }
  else if (hp < 4) { g = x; b = c; }
  else if (hp < 5) { r = x; b = c; }
  else { r = c; b = x; }
  const m2 = l - c / 2;
  const toHex = (v: number) => Math.round((v + m2) * 255).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function hexToHslTriple(hex: string): string {
  const clean = hex.replace("#", "").trim();
  if (clean.length !== 6) return "0 0% 0%";
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0));
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}
